import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../security";
import { addNotification } from "../../notifications/notificationsSlice";
import { AttorneyNote, AttorneyNotesResponse, AttorneyNotesView } from "./model/attorneyNote";

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;

export const attorneyNotesApi = createApi({
  reducerPath: "attorneyNotesApi",
  tagTypes: ["AttorneyNotes", "AttorneyNote"],
  baseQuery: fetchBaseQuery({
    baseUrl: apiServerUrl,
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getAttorneyNotes: builder.query<AttorneyNotesView, string>({
      query: (qs) => `/api/v2/attorney_notes?${qs}`,
      transformResponse: (response: AttorneyNotesResponse): AttorneyNotesView => ({
        data: response.attorney_notes,
        count: response.meta?.count ?? 0
      }),
      providesTags: ["AttorneyNotes"]
    }),
    getAttorneyNote: builder.query<AttorneyNote, string>({
      query: (id) => `/api/v2/attorney_notes/${id}`,
      transformResponse: (response: { attorney_note: AttorneyNote }) => response.attorney_note,
      providesTags: (result, error, id) => [{ type: "AttorneyNote", id }]
    }),
    createAttorneyNote: builder.mutation<AttorneyNote, { attorney_note: Partial<AttorneyNote> }>({
      query: (body) => ({
        url: "/api/v2/attorney_notes",
        method: "POST",
        body
      }),
      invalidatesTags: ["AttorneyNotes"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Attorney note created successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to create attorney note",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    updateAttorneyNote: builder.mutation<AttorneyNote, { id: string; attorney_note: Partial<AttorneyNote> }>({
      query: ({ id, attorney_note }) => ({
        url: `/api/v2/attorney_notes/${id}`,
        method: "PATCH",
        body: { attorney_note }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AttorneyNote", id },
        "AttorneyNotes"
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Attorney note updated successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to update attorney note",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    deleteAttorneyNote: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/v2/attorney_notes/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["AttorneyNotes"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Attorney note deleted successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to delete attorney note",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    })
  })
});

export const {
  useGetAttorneyNotesQuery,
  useGetAttorneyNoteQuery,
  useCreateAttorneyNoteMutation,
  useUpdateAttorneyNoteMutation,
  useDeleteAttorneyNoteMutation
} = attorneyNotesApi;
